import React, { useState, useEffect } from 'react';
import { useStore } from 'context/store-context';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { LoadingScreen } from 'components/LoadingScreen';
import { format, startOfDay } from 'date-fns';
import {
  NotificationTypes,
  apiErrorHandler,
  getDateXDaysAgo,
  getCurrentTierInfo,
} from 'utils';
import { withNotification } from 'components/Notification';
import { GET_PROTECTED_ORDERS_COUNT } from 'gql/queries';
import { CANCEL_STORE_PLAN } from 'gql/mutations';
import PaymentMethod from 'components/Billing/Settings/PaymentMethod';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router';
import SettingsViewWrapper from 'components/SettingsWrapperView';

export const BillingProfile = withNotification(({ showNotification }: any) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { storeId } = useStore();

  const [
    cancelStorePlan,
    { error: cancelPlanError, loading: cancelPlanLoading },
  ] = useMutation(CANCEL_STORE_PLAN, {
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
  });

  const [getProtectedOrdersCount, { data: countData, loading }] = useLazyQuery(
    GET_PROTECTED_ORDERS_COUNT,
  );

  useEffect(() => {
    const date = new Date(),
      y = date.getFullYear(),
      m = date.getMonth();
    const firstDayOfMonth = new Date(y, m, 1);

    const dateTomorrow = new Date(
      new Date(date).setDate(new Date(date).getDate() + 1),
    );
    storeId &&
      getProtectedOrdersCount({
        variables: {
          filters: {
            storeId: { _eq: storeId },
            protectionTotal: { _is_null: false },
            _and: [
              {
                createdAt: {
                  _gte: format(
                    startOfDay(firstDayOfMonth ?? getDateXDaysAgo(30)),
                    'yyyy-MM-dd',
                  ),
                },
              },
              {
                createdAt: {
                  _lte: format(
                    startOfDay(dateTomorrow ?? getDateXDaysAgo(30)),
                    'yyyy-MM-dd',
                  ),
                },
              },
            ],
          },
        },
      });
  }, [getProtectedOrdersCount, storeId]);

  const handleCancelPlan = () => {
    cancelStorePlan({
      variables: { input: { storeId } },
    })
      .then(() => {
        setShowConfirmationModal(!showConfirmationModal);
        return showNotification(
          NotificationTypes.success,
          'Store plan cancelled successfully',
        );
      })
      .catch((err) => {
        setShowConfirmationModal(!showConfirmationModal);
        return showNotification(
          NotificationTypes.error,
          cancelPlanError ? cancelPlanError.message : 'Could not cancel plan',
        );
      });
  };

  const protectedOrdersCount = countData?.ordersAggregate?.aggregate.count;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('payment-method-updated')) {
      showNotification(
        NotificationTypes.success,
        'Payment method successfully updated!',
      );
      queryParams.delete('payment-method-updated');
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, showNotification]);

  if (loading) return <LoadingScreen />;

  return (
    <SettingsViewWrapper activeNav="billing/profile" sectionId="billing">
      <div>
        <ConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={(value) => setShowConfirmationModal(value)}
          title={`Are you sure you want to cancel your plan?`}
          handleConfirm={handleCancelPlan}
          subMessage="The plan will end at the the end of the billing cycle"
          btnText="Cancel plan"
          mainBtnClassname="text-white"
          type={NotificationTypes.error}
          isLoading={cancelPlanLoading}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-6">
          <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
            Billing Profile
          </h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-3">
          <div className="settings">
            <div className="border rounded-lg p-6 bg-white shadow-sm mb-4">
              <h2 className="text-xl font-semibold mb-4">Plan Details</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Plan</span>
                  <span className="font-medium">{`Billing: Tier ${
                    getCurrentTierInfo(protectedOrdersCount).level
                  }`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Billing Period</span>
                  <span className="font-medium">Monthly</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Next Billing Date</span>
                  <span className="font-medium">
                    {moment()
                      .add(1, 'month')
                      .startOf('month')
                      .format('DD MMM, YYYY')}
                  </span>
                </div>
              </div>
            </div>

            <PaymentMethod />
          </div>
        </div>
      </div>
    </SettingsViewWrapper>
  );
});
